import { useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { fmtMoney, STATUS_LABELS, PLATFORMS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, X } from "lucide-react";

const newItem = () => ({ uid: crypto.randomUUID(), product_name: "", category: "Other", quantity: 1, unit_price: "" });
const initialOrder = () => ({
  order_date: new Date().toISOString().slice(0, 10), status: "paid",
  source_platform: "Manual Entry", shipping: "", tax: "", discount: "", items: [newItem()],
});

export const AddOrderDialog = ({ open, onOpenChange, customerId, categories, onSaved }) => {
  const [order, setOrder] = useState(initialOrder);

  const patch = (p) => setOrder((o) => ({ ...o, ...p }));
  const patchItem = (uid, p) => setOrder((o) => ({ ...o, items: o.items.map((it) => (it.uid === uid ? { ...it, ...p } : it)) }));
  const removeItem = (uid) => setOrder((o) => ({ ...o, items: o.items.filter((it) => it.uid !== uid) }));

  const total = order.items.reduce((s, i) => s + (parseFloat(i.unit_price) || 0) * (parseInt(i.quantity) || 1), 0)
    + (parseFloat(order.shipping) || 0) + (parseFloat(order.tax) || 0) - (parseFloat(order.discount) || 0);

  const save = async () => {
    const items = order.items.filter((i) => i.product_name.trim());
    if (items.length === 0) { toast.error("Please add at least one item with a name."); return; }
    try {
      await api.post("/orders", {
        customer_id: customerId, order_date: order.order_date, status: order.status,
        source_platform: order.source_platform,
        shipping: parseFloat(order.shipping) || 0, tax: parseFloat(order.tax) || 0,
        discount: parseFloat(order.discount) || 0,
        items: items.map((i) => ({ product_name: i.product_name, category: i.category, quantity: parseInt(i.quantity) || 1, unit_price: parseFloat(i.unit_price) || 0 })),
      });
      toast.success("Order added.");
      onOpenChange(false);
      setOrder(initialOrder());
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Add an Order</DialogTitle></DialogHeader>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-1.5"><Label>Order date</Label><Input type="date" data-testid="order-date-input" value={order.order_date} onChange={(e) => patch({ order_date: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Status</Label>
            <Select value={order.status} onValueChange={(v) => patch({ status: v })}>
              <SelectTrigger data-testid="order-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Where from</Label>
            <Select value={order.source_platform} onValueChange={(v) => patch({ source_platform: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-3">
          <Label>Items</Label>
          {order.items.map((item, idx) => (
            <div key={item.uid} className="grid grid-cols-12 gap-2 items-center">
              <Input className="col-span-5" placeholder="Product or donation name" data-testid={`item-name-${idx}`} value={item.product_name} onChange={(e) => patchItem(item.uid, { product_name: e.target.value })} />
              <div className="col-span-3">
                <Select value={item.category} onValueChange={(v) => patchItem(item.uid, { category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Input className="col-span-1" type="number" min="1" value={item.quantity} onChange={(e) => patchItem(item.uid, { quantity: e.target.value })} />
              <Input className="col-span-2" type="number" step="0.01" placeholder="Price" data-testid={`item-price-${idx}`} value={item.unit_price} onChange={(e) => patchItem(item.uid, { unit_price: e.target.value })} />
              <Button variant="ghost" size="sm" className="col-span-1" disabled={order.items.length === 1} onClick={() => removeItem(item.uid)}><X className="h-4 w-4" /></Button>
            </div>
          ))}
          <Button variant="secondary" size="sm" onClick={() => setOrder((o) => ({ ...o, items: [...o.items, newItem()] }))} data-testid="add-item-btn">
            <Plus className="h-4 w-4 mr-1" /> Add another item
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-1.5"><Label>Shipping ($)</Label><Input type="number" step="0.01" value={order.shipping} onChange={(e) => patch({ shipping: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Tax ($)</Label><Input type="number" step="0.01" value={order.tax} onChange={(e) => patch({ tax: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Discount ($)</Label><Input type="number" step="0.01" value={order.discount} onChange={(e) => patch({ discount: e.target.value })} /></div>
        </div>
        <p className="text-right font-serif text-xl font-semibold" data-testid="order-total-preview">Total: {fmtMoney(total)}</p>
        <DialogFooter>
          <Button onClick={save} data-testid="save-order-btn">Save order</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
